import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Download, History, Search, X } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { AttachmentChips } from '../components/gsoc/Attachments';

const selectCls = 'h-10 px-3 text-sm rounded-md border border-line bg-surface text-fg';
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'medium' }) : '—');
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const EMPTY = { operator: '', note_id: '', date_from: '', date_to: '', shift: '', client_id: '' };

export default function GSOCPassOnAudit() {
  const [filters, setFilters] = useState(EMPTY);
  const [operatorInput, setOperatorInput] = useState('');
  const [data, setData] = useState({ total: 0, acknowledgements: [] });
  const [notes, setNotes] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [revisions, setRevisions] = useState({});

  useEffect(() => {
    api.get('/gsoc/pass-on', { params: { include_inactive: true } })
      .then(({ data }) => setNotes(data)).catch(() => {});
    api.get('/gsoc/shifts').then(({ data }) => setShifts(data.filter((s) => s.is_active))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...filters, page_size: 200 };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    api.get('/gsoc/pass-on-acknowledgements', { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load the acknowledgement record'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await api.get('/gsoc/pass-on-acknowledgements/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PassOn_Acknowledgements_${filters.operator || 'all'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  const toggleRow = async (row) => {
    if (openRow === row.id) { setOpenRow(null); return; }
    setOpenRow(row.id);
    if (!revisions[row.note_id]) {
      try {
        const { data } = await api.get(`/gsoc/pass-on/${row.note_id}/revisions`);
        setRevisions((r) => ({ ...r, [row.note_id]: data }));
      } catch { /* a removed note simply has no trail to show */ }
    }
  };

  return (
    <div className="space-y-5" data-testid="gsoc-passon-audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Acknowledgement Record</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">
            Every pass-on note an operator signed off on, in the exact wording they were shown at the time.
            Filter to one person and a date range, then export it as the record.
          </p>
        </div>
        <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="gsoc-ack-export-btn">
          <Download className="w-4 h-4 mr-2" aria-hidden="true" />{exporting ? 'Building...' : 'Export Record'}
        </Button>
      </div>

      <div className="rounded-lg border border-line bg-surface p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px] flex-1">
            <Label className="text-sm">Operator (name or email)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={operatorInput} placeholder="e.g. chadbradley" data-testid="gsoc-ack-operator-input"
                     onChange={(e) => setOperatorInput(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && setFilters((f) => ({ ...f, operator: operatorInput }))} />
              <Button variant="outline" size="icon" data-testid="gsoc-ack-search-btn"
                      onClick={() => setFilters((f) => ({ ...f, operator: operatorInput }))}>
                <Search className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
          <div className="min-w-[240px]">
            <Label className="text-sm">Note</Label>
            <select className={`${selectCls} mt-1 w-full`} value={filters.note_id} data-testid="gsoc-ack-note-filter"
                    onChange={(e) => setFilters((f) => ({ ...f, note_id: e.target.value }))}>
              <option value="">All notes</option>
              {notes.map((n) => <option key={n.id} value={n.id}>{n.title}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm">From</Label>
            <Input type="date" value={filters.date_from} className="mt-1" data-testid="gsoc-ack-from"
                   onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} />
          </div>
          <div>
            <Label className="text-sm">To</Label>
            <Input type="date" value={filters.date_to} className="mt-1" data-testid="gsoc-ack-to"
                   onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} />
          </div>
          <div>
            <Label className="text-sm">Shift</Label>
            <select className={`${selectCls} mt-1`} value={filters.shift} data-testid="gsoc-ack-shift"
                    onChange={(e) => setFilters((f) => ({ ...f, shift: e.target.value }))}>
              <option value="">All shifts</option>
              {shifts.map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
            </select>
          </div>
          <Button variant="ghost" data-testid="gsoc-ack-clear"
                  onClick={() => { setFilters(EMPTY); setOperatorInput(''); }}>
            <X className="w-4 h-4 mr-1" aria-hidden="true" />Clear
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-line bg-surface overflow-hidden">
        <div className="px-4 py-2 text-sm border-b border-line text-fg-muted" data-testid="gsoc-ack-count">
          {loading ? 'Loading...' : `${data.total} acknowledgement${data.total === 1 ? '' : 's'}`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-sunken">
                <th className="text-left px-4 py-3 font-medium text-fg-muted">Operator</th>
                <th className="text-left px-4 py-3 font-medium text-fg-muted">Acknowledged</th>
                <th className="text-left px-4 py-3 font-medium text-fg-muted">Shift</th>
                <th className="text-left px-4 py-3 font-medium text-fg-muted">Note (as acknowledged)</th>
                <th className="text-left px-4 py-3 font-medium text-fg-muted">Scope</th>
              </tr>
            </thead>
            <tbody data-testid="gsoc-ack-rows">
              {data.acknowledgements.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-b border-line-subtle hover:bg-surface-hover cursor-pointer"
                      onClick={() => toggleRow(r)} data-testid={`gsoc-ack-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-fg font-medium">{r.operator_name}</p>
                      <p className="text-xs text-fg-muted">{r.operator_email || '—'}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtStamp(r.acknowledged_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.shift_label}<br />
                      <span className="text-xs text-fg-muted">{fmtDate(r.shift_date)}</span></td>
                    <td className="px-4 py-3 max-w-lg">
                      <div className="text-fg flex items-center gap-2 flex-wrap">
                        {r.note_is_urgent && <Badge className="bg-[hsl(var(--danger))] text-white border-0">URGENT</Badge>}
                        {r.note_title}
                        {r.changed_since_ack && (
                          <Badge className="bg-warning-bg text-warning border border-warning-border"
                                 data-testid={`gsoc-ack-changed-${r.id}`}>edited since</Badge>
                        )}
                        {!r.snapshot_available && (
                          <Badge className="bg-surface-sunken text-fg-muted border border-line">no snapshot</Badge>
                        )}
                      </div>
                      <p className="text-xs text-fg-secondary truncate">{r.note_body}</p>
                      {r.note_attachments?.length > 0 && (
                        <p className="text-xs text-fg-muted mt-0.5" data-testid={`gsoc-ack-attcount-${r.id}`}>
                          {r.note_attachments.length} attachment{r.note_attachments.length === 1 ? '' : 's'}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-fg-muted whitespace-nowrap">
                      {r.client_name}<br />{r.site_name}
                    </td>
                  </tr>
                  {openRow === r.id && (
                    <tr className="border-b border-line bg-surface-sunken">
                      <td colSpan={5} className="px-4 py-4 space-y-3" data-testid={`gsoc-ack-detail-${r.id}`}>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-fg-muted">Wording acknowledged by {r.operator_name}</p>
                          <p className="mt-1 text-fg whitespace-pre-wrap">{r.note_body}</p>
                          {(r.note_work_order_number || r.note_account_number) && (
                            <p className="mt-2 text-xs text-fg-muted flex flex-wrap gap-x-4">
                              {r.note_work_order_number && <span>Work Order # <span className="text-fg font-medium">{r.note_work_order_number}</span></span>}
                              {r.note_account_number && <span>Account # <span className="text-fg font-medium">{r.note_account_number}</span></span>}
                            </p>
                          )}
                        </div>
                        {r.note_attachments?.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-fg-muted mb-1">Attachments shown at the time</p>
                            <AttachmentChips items={r.note_attachments} testIdPrefix={`gsoc-ack-att-${r.id}`} />
                          </div>
                        )}
                        {r.changed_since_ack && (
                          <div className="rounded-md border border-warning-border bg-warning-bg px-3 py-2">
                            <p className="text-sm font-medium text-fg flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                              This note was edited after the acknowledgement
                            </p>
                            <p className="text-xs text-fg-secondary mt-1">Current wording: {r.current_body || '(note removed)'}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs uppercase tracking-wide text-fg-muted flex items-center gap-1">
                            <History className="w-3 h-3" aria-hidden="true" />Edit history
                          </p>
                          {(revisions[r.note_id] || []).length === 0 ? (
                            <p className="text-sm text-fg-muted mt-1">No edits recorded for this note.</p>
                          ) : (
                            <ul className="mt-1 space-y-2">
                              {revisions[r.note_id].map((rev) => (
                                <li key={rev.id} className="text-sm">
                                  <span className="text-fg-muted">{fmtStamp(rev.changed_at)} · {rev.changed_by_name} changed </span>
                                  <span className="text-fg">{rev.changed_fields.join(', ')}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <p className="text-xs text-fg-muted">Note status now: {r.note_status}</p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!loading && data.acknowledgements.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-fg-muted" data-testid="gsoc-ack-empty">
                  No acknowledgements match these filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
